import { z, type ZodType } from "zod";
import { API_PREFIX } from "@shared/constants";

/** The error envelope every API failure uses — see docs/06-api.md#errors. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/**
 * A session can end between two clicks — it expires, an admin revokes the
 * allow-list entry, someone signs out in another tab — and the next call comes
 * back 401. Every caller handling that itself would mean every caller getting it
 * slightly wrong, so it is announced once from here and `AuthProvider` listens.
 *
 * The auth routes are excluded: a 401 from `GET /auth/me` on boot is the answer
 * to a question, not the loss of a session, and one from `POST /auth/login` is a
 * wrong password.
 */
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function announceUnauthorized(path: string) {
  if (path.startsWith("/auth/")) return;
  for (const listener of unauthorizedListeners) listener();
}

/**
 * One place where a response becomes a typed value. The schema is not decoration:
 * a server that starts returning a differently shaped payload fails here, loudly,
 * instead of leaking `undefined` into a chart three components away.
 */
export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = options;

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...rest,
    credentials: "same-origin",
    headers: {
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, "MALFORMED_RESPONSE", "The server sent invalid JSON.");
    }
  }

  if (!response.ok) {
    if (response.status === 401) announceUnauthorized(path);
    const envelope = payload as { error?: { code?: string; message?: string; details?: unknown } };
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? "UNKNOWN",
      envelope?.error?.message ?? `Request failed with status ${response.status}.`,
      envelope?.error?.details,
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      "MALFORMED_RESPONSE",
      "The server sent an unexpected payload.",
      parsed.error.issues,
    );
  }
  return parsed.data;
}

export const apiGet = <T>(path: string, schema: ZodType<T>): Promise<T> =>
  apiRequest(path, schema, { method: "GET" });

export const apiPost = <T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> =>
  apiRequest(path, schema, { method: "POST", body });

export const apiPatch = <T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> =>
  apiRequest(path, schema, { method: "PATCH", body });

export const apiPut = <T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> =>
  apiRequest(path, schema, { method: "PUT", body });

export const apiDelete = <T>(path: string, schema: ZodType<T>): Promise<T> =>
  apiRequest(path, schema, { method: "DELETE" });

/** For the 204s. An empty body parses to `undefined`, which is what this accepts. */
export const noContentSchema = z.undefined();

/**
 * Query strings, with absent-means-absent. `?q=&status=` and no query at all
 * should be the same request; building the string by hand is how they stop
 * being.
 */
export function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}
